import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import InnerHTML from "dangerously-set-html-content";
import { useEffect, useState } from "react";
import {
  AutocompleteElement,
  FormContainer,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import HorizontalVerticalTogglerLayout from "src/shared/Layouts/HorizontalVerticalTogglerLayout";
import {
  useCountryNumericCodeData,
  usePhoneCodeData,
} from "./data-access/useCountryNumericCodeData";
import { useCreditCardFormData } from "./data-access/useCreditCardFormData";
import useSubmitCreditCardMutation from "./data-access/useSubmitCreditCardMutation";
import useGetMerchantNotification from "./data-access/useGetMerchantNotification";
import useGetTDSHandleContinue from "./data-access/useGetTDSHandleContinue";
import useGetParesResult from "./data-access/useGetParesResult";
import useTdsCompletePares from "./data-access/useTdsCompletePares";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import InfoMessage from "src/shared/components/InfoMessage";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import PaymentCheckbox from "src/violationCase/components/PaymentCheckbox";

type TPaymentFormProps = {
  mode:
    | "subscription"
    | "app"
    | "cert"
    | "namePurchase"
    | "namePurchaseApplication"
    | "violationCase";
  appId?: string | null;
  year?: string;
  relatedId?: string;
  relatedType?: string;
  finePaymentId?: number;
};
function PaymentForm(props: TPaymentFormProps) {
  const { mode, appId, year, relatedId, relatedType, finePaymentId } = props;
  const creditCardImages = [
    { alt: "Piraeus", path: "/images/peiraios-600x600.png" },
    { alt: "Maestro", path: "/images/NicePng_mastercard-png_1367187.png" },
    { alt: "MasterCard SecureCode", path: "/images/MasterCard2.png" },
    { alt: "MasterCard", path: "/images/MasterCardSecureCode.png" },
    {
      alt: "Verified by Visa",
      path: "/images/NicePng_mastercard-png_1367091.png",
    },
    { alt: "Visa", path: "/images/VISA2.jpg" },
  ];

  const { data: backendData, isLoading } = useCreditCardFormData(
    mode,
    appId,
    year,
    finePaymentId,
  );
  const { t } = useTranslation();
  const [Dialog3DSOpen, setDialog3DSOpen] = useState(false);
  const [enableMerchantRef, setEnableMerchantRef] = useState(false);
  const [enableHandleContinue, setEnableHandleContinue] = useState(false);
  const [enableParesPoll, setEnableParesPoll] = useState(false);
  const [enableCompletePares, setEnableCompletePares] = useState(false);
  const [isAcsChallenge, setIsAcsChallenge] = useState(false);
  const [paymentAccepted, setPaymentAccepted] = useState(false);
  const validationSchema = yup.object().shape({
    m02_id: yup.string().nullable(),
    countryCode: yup
      .object()
      .shape({
        label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
        value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      })
      .nullable(),
    phoneCode: yup.string().nullable(),
    navigatorLanguage: yup.string().nullable(),
    navigatorJavaEnabled: yup.boolean().nullable(),
    navigatorJsEnabled: yup.boolean().nullable(),
    screenColorDepth: yup.number().nullable(),
    screenWidth: yup.number().nullable(),
    screenHeight: yup.number().nullable(),
    timezoneOffset: yup.number().nullable(),
    cardType: yup.object().shape({
      label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
    }),
    cardNumber: yup
      .string()
      .required(t("Το πεδίο είναι υποχρεωτικό"))
      .matches(
        /^\d{13,19}$/,
        t("Ο αριθμός κάρτας αποτελείται από 13 έως 19 ψηφία"),
      ),
    expMonth: yup.object().shape({
      label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
    }),
    expYear: yup.object().shape({
      label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
    }),
    CVV: yup
      .string()
      .required(t("Το πεδίο είναι υποχρεωτικό"))
      .matches(/^\d{3}$/, t("Ο κωδικός ασφαλείας αποτελείται από 3 ψηφία")),
    customerName: yup
      .string()
      .required(t("Το πεδίο είναι υποχρεωτικό"))
      .matches(/^[a-zA-Z0-9 /:_().,+-]{2,45}$/, t("Το όνομα δεν είναι έγκυρο")),
    email: yup
      .string()
      .required(t("Το πεδίο είναι υποχρεωτικό"))
      .matches(
        /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(.[a-zA-Z0-9-]+)+$/,
        t("Το email δεν είναι έγκυρο."),
      ),
    countryState: yup.object().shape({
      label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
    }),
    city: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
    address: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
    zipCode: yup.number().required(t("Το πεδίο είναι υποχρεωτικό")),
    mobilePhone: yup
      .string()
      .required(t("Το πεδίο είναι υποχρεωτικό"))
      .matches(/^\d{1,15}$/, t("Το τηλέφωνο αποτελείται από το πολύ 15 ψηφία")),
    homePhone: yup
      .string()
      .required(t("Το πεδίο είναι υποχρεωτικό"))
      .matches(/^\d{1,15}$/, t("Το τηλέφωνο αποτελείται από το πολύ 15 ψηφία")),
  });
  const formContext = useForm({
    resolver: yupResolver(validationSchema),
  });
  const [iframeContent, setIframeContent] = useState("");
  const { data: countrySubdivision, isLoading: IsLoadingCountry } =
    useCountryNumericCodeData(formContext.watch("countryCode")?.value ?? "300");
  const { data: phoneCodeData, isLoading: IsLoadingPhoneCode } =
    usePhoneCodeData(formContext.watch("countryCode")?.value ?? "300");

  const {
    mutateAsync: cardProcess,
    isLoading: isLoadingSumbit,
    data: SubmitData,
  } = useSubmitCreditCardMutation(
    setDialog3DSOpen,
    setIframeContent,
    setEnableMerchantRef,
    setEnableParesPoll,
    setIsAcsChallenge,
    relatedId,
    relatedType,
  );

  const { data: TDSHandleContinue } = useGetTDSHandleContinue({
    enabled: enableHandleContinue,
    merchantReference: SubmitData?.data?.merchantReference,
    setIframeContent,
    setDialog3DSOpen,
    setEnableParesPoll,
    setIsAcsChallenge,
    relatedId,
    relatedType,
  });

  useGetParesResult({
    enabled: enableParesPoll,
    merchantReference: SubmitData?.data?.merchantReference,
    setEnableParesPoll,
    setEnableCompletePares,
  });

  useTdsCompletePares({
    enabled: enableCompletePares,
    merchantReference: SubmitData?.data?.merchantReference,
    setDialog3DSOpen,
    relatedId,
    relatedType,
  });

  const { data: merchantNotification, isLoading: merchantLoading } =
    useGetMerchantNotification({
      enabled: enableMerchantRef,
      merchantReference: SubmitData?.data?.merchantReference,
      setEnableHandleContinue,
      setEnableMerchantRef,
    });

  useEffect(() => {
    //after ten seconds of checking merchant ref, enable handle Continue query
    let timeout: number;
    if (SubmitData?.data?.tdsMethodContent) {
      timeout = setTimeout(() => {
        setEnableHandleContinue(true);
      }, 10000);
    }
    return () => clearTimeout(timeout);
  }, [SubmitData]);

  useEffect(() => {
    if (!isLoading && backendData?.data) {
      formContext.reset({
        countryCode: {
          value: backendData.data.countries?.find(
            (country: { d06_numeric_code: string }) =>
              country.d06_numeric_code === "300",
          ).d06_numeric_code,
          label: backendData.data.countries?.find(
            (country: { d06_numeric_code: string }) =>
              country.d06_numeric_code === "300",
          ).d06_descr,
        },
      });
    }
  }, [backendData, isLoading]);

  useEffect(() => {
    if (
      (!IsLoadingPhoneCode && phoneCodeData?.data?.countryPhoneCode) ||
      formContext.watch("phoneCode") === null ||
      formContext.watch("phoneCode") === undefined
    ) {
      formContext.setValue("phoneCode", phoneCodeData?.data?.countryPhoneCode);
    }
  }, [IsLoadingPhoneCode, phoneCodeData, formContext.watch("phoneCode")]);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <Typography variant="h4">{t("Πληρωμή μέσω κάρτας")}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {creditCardImages.map((img, index) => (
              <img
                key={index}
                src={img.path}
                alt={img.alt}
                style={{
                  width:
                    img.alt === "Piraeus"
                      ? "2rem"
                      : img.alt === "MasterCard"
                        ? "5rem"
                        : img.alt === "Visa"
                          ? "5rem"
                          : img.alt === "Maestro"
                            ? "2.5rem"
                            : "3rem",
                  height: "auto",
                  objectFit: "contain",
                  margin: "0 0.1rem",
                }}
              />
            ))}
          </Box>
        </Box>

        <FormContainer
          formContext={formContext}
          onSuccess={async (data) => {
            data.m02_id = appId;
            data.phoneCode = phoneCodeData?.data?.countryPhoneCode + "-";
            data.navigatorLanguage = navigator.language;
            data.navigatorJavaEnabled = navigator.javaEnabled();
            data.navigatorJsEnabled = true;
            data.screenColorDepth = screen.colorDepth;
            data.screenHeight = screen.height;
            data.screenWidth = screen.width;
            data.timezoneOffset = new Date().getTimezoneOffset();
            // reset 3DS flow state so a retry (without page refresh) starts clean and does not
            // reuse a previous attempt's enabled flags / cached queries
            setEnableHandleContinue(false);
            setEnableParesPoll(false);
            setEnableCompletePares(false);
            setIsAcsChallenge(false);
            try {
              const response = await cardProcess(data);
              if (response.data) {
                setDialog3DSOpen(true);
                setEnableMerchantRef(true);
              }
            } catch (error) {
              const status = (error as AxiosError)?.response?.status;
              if (status === undefined || status >= 500) {
                throw new Error(
                  `Credit card payment failed with status ${status ?? "unknown"}`,
                  { cause: error },
                );
              }
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "1rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  padding: { xs: "none", sm: "1rem" },
                  width: "100%",
                }}
              >
                <Typography variant="h5">{t("Στοιχεία κάρτας")}</Typography>
                <HorizontalVerticalTogglerLayout>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "1em",
                      minWidth: "70%",
                    }}
                  >
                    <AutocompleteElement
                      label={t("Τύπος Κάρτας")}
                      name={"cardType"}
                      loading={isLoading}
                      options={
                        backendData?.data?.cardTypes?.map(
                          (cardType: any, index: number) => {
                            return {
                              value: cardType.cd,
                              label: cardType.descr,
                            };
                          },
                        ) || []
                      }
                      autocompleteProps={{
                        sx: {
                          width: "100%",
                        },
                      }}
                    />

                    <Tooltip
                      title={t(
                        "Επιλέξτε τον τύπο της κάρτας σας χρησιμοποιώντας τις παρακάτω επιλογές.",
                      )}
                    >
                      <HelpOutlinedIcon color="primary" />
                    </Tooltip>
                  </Box>
                </HorizontalVerticalTogglerLayout>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`cardNumber`}
                    label={t("Αριθμός Κάρτας")}
                    sx={{ width: "100%" }}
                    placeholder={t("από 13 έως 19 ψηφία χωρίς κενά.")}
                    inputProps={{
                      pattern: "^d{13,19}$",
                      maxLength: 19,
                      "data-testid": "keimenoTest",
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε τον αριθμό (13 έως 19 ψηφία) χωρίς κενά που βρίσκεται στο μπροστινό μέρος της VISA ή MASTERCARD",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: { xs: "100%", sm: "50%" },
                      gap: "1rem",
                    }}
                  >
                    <Typography>{t("Ημερομηνία Λήξης")}</Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        textAlign: "center",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "1rem",
                          width: "40%",
                        }}
                      >
                        <AutocompleteElement
                          required
                          label={t("Μήνας")}
                          name={"expMonth"}
                          loading={isLoading}
                          options={
                            backendData?.data?.months?.map((month: any) => {
                              return { value: month.cd, label: month.descr };
                            }) || []
                          }
                          autocompleteProps={{
                            sx: {
                              width: "100%",
                            },
                          }}
                        />
                        <Tooltip
                          title={t(
                            "Επιλέξτε την ημερομηνία λήξης της κάρτας σας χρησιμοποιώντας τις παρακάτω επιλογές",
                          )}
                        >
                          <HelpOutlinedIcon color="primary" />
                        </Tooltip>
                      </Box>

                      <Typography
                        sx={{ width: "5%", fontWeight: "lighter" }}
                        variant="h4"
                        component="p"
                      >
                        /
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "1em",
                          width: "40%",
                        }}
                      >
                        <AutocompleteElement
                          required
                          label={t("Έτος")}
                          name={"expYear"}
                          loading={isLoading}
                          options={
                            backendData?.data?.years?.map((year: any) => {
                              return { value: year.cd, label: year.descr };
                            }) || []
                          }
                          autocompleteProps={{
                            sx: {
                              width: "100%",
                            },
                            autoComplete: false,
                          }}
                        />
                        <Tooltip
                          title={t(
                            "Επιλέξτε την ημερομηνία λήξης της κάρτας σας χρησιμοποιώντας τις παρακάτω επιλογές",
                          )}
                        >
                          <HelpOutlinedIcon color="primary" />
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignSelf: "end",
                      flexDirection: "row",
                      alignItems: "center",
                      width: { xs: "100%", sm: "40%" },
                      gap: "1em",
                    }}
                  >
                    <TextFieldElement
                      type="password"
                      required
                      name={`CVV`}
                      label={t("Κωδικός Ασφαλείας Κάρτας (CVV)")}
                      sx={{ width: "100%" }}
                      inputProps={{ pattern: "^d{3}$", maxLength: 3 }}
                    />
                    <Tooltip
                      title={t(
                        "Εισάγετε τον κωδικό ασφαλείας(CVV) της MasterCard ή Visa ο οποίος αποτελείται από τα 3 τελευταία ψηφία που βρίσκονται στο πίσω μέρος της πιστωτικής σας κάρτας",
                      )}
                    >
                      <HelpOutlinedIcon color="primary" />
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1em",
                  }}
                >
                  <TextFieldElement
                    required
                    type="text"
                    name={`customerName`}
                    label={t("Όνομα και Επώνυμο Κατόχου")}
                    sx={{ width: "100%" }}
                    inputProps={{ pattern: "^[a-zA-Z0-9 /:_().,+-]{2,45}$" }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε το όνομα κατόχου της κάρτας ακριβώς όπως αναγράφεται χαμηλά στο μπροστινό μέρος της κάρτας σας.",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  padding: { xs: "none", sm: "1rem" },
                  width: "100%",
                }}
              >
                <Typography variant="h5">
                  {t("Στοιχεία επικοινωνίας κατόχου κάρτας")}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`email`}
                    label={t("Email")}
                    sx={{ width: "100%" }}
                    placeholder="info@mycompany.gr"
                    inputProps={{
                      pattern:
                        "^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9-]+(.[a-zA-Z0-9-]+)+$",
                      maxLength: 254,
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε ένα email επικοινωνίας του κατόχου της κάρτας",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`mobilePhone`}
                    label={t(
                      "Κινητό τηλέφωνο κατόχου κάρτας (χωρίς τον κωδικό της χώρας μπροστά)",
                    )}
                    sx={{ width: "100%" }}
                    placeholder={`${t("π.χ.")} 69722222222`}
                    inputProps={{ pattern: "^d{1,15}$" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {formContext.watch("phoneCode") === undefined ||
                          IsLoadingPhoneCode ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <CircularProgress size={25} />
                            </Box>
                          ) : (
                            formContext.watch("phoneCode") + "-"
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε το κινητό σας τηλέφωνο χωρίς τον κωδικό της χώρας μπροστά, ο οποίος θα προστεθεί αυτόματα στην αποστολή προς την τράπεζα. Επιτρέπονται έως 15 ψηφία χωρίς κενά.",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`homePhone`}
                    label={t(
                      "Σταθερό τηλέφωνο κατόχου κάρτας (χωρίς τον κωδικό της χώρας μπροστά)",
                    )}
                    sx={{ width: "100%" }}
                    placeholder={`${t("π.χ.")} 2103288000`}
                    inputProps={{ pattern: "^d{1,15}$" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {formContext.watch("phoneCode") === undefined ||
                          IsLoadingPhoneCode ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <CircularProgress size={25} />
                            </Box>
                          ) : (
                            formContext.watch("phoneCode") + "-"
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε το σταθερό σας τηλέφωνο χωρίς τον κωδικό της χώρας μπροστά, ο οποίος θα προστεθεί αυτόματα στην αποστολή προς την τράπεζα. Επιτρέπονται έως 15 ψηφία χωρίς κενά.",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2rem",
                  padding: { xs: "none", sm: "1rem" },
                  width: "100%",
                }}
              >
                <Typography variant="h5">
                  {t("Στοιχεία διεύθυνσης χρέωσης")}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <AutocompleteElement
                    required
                    label={t("Χώρα")}
                    name={"countryCode"}
                    loading={isLoading}
                    options={
                      backendData?.data?.countries?.map((country: any) => {
                        return {
                          value: country.d06_numeric_code,
                          label: country.d06_descr,
                        };
                      }) || []
                    }
                    autocompleteProps={{
                      sx: {
                        width: "100%",
                      },
                      getOptionLabel: (option) => option.label,
                      isOptionEqualToValue: (option, value) =>
                        option.value === value.value,
                    }}
                  />

                  <Tooltip title={t("Επιλέξτε τη χώρα της διεύθυνσης χρέωσης")}>
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <AutocompleteElement
                    required
                    label={t("Περιφέρεια")}
                    name={"countryState"}
                    loading={IsLoadingCountry}
                    options={
                      countrySubdivision?.data?.countrySubdivisions?.map(
                        (countrySub: any) => {
                          return {
                            value: countrySub.code,
                            label: countrySub.descr,
                          };
                        },
                      ) || []
                    }
                    autocompleteProps={{
                      sx: {
                        width: "100%",
                      },
                    }}
                  />

                  <Tooltip
                    title={t("Επιλέξτε την περιφέρεια της διεύθυνσης χρέωσης")}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`city`}
                    label={t("Πόλη")}
                    sx={{ width: "100%" }}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε την πόλη της διεύθυνσης χρέωσης. Επιτρέπονται έως 50 χαρακτήρες χωρίς τόνους Ελληνικοί και Αγγλικοί, πεζοί και κεφαλαίοι, το κενό και τα εξής σύμβολα: /:_().,+-",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`address`}
                    label={t("Διεύθυνση")}
                    sx={{ width: "100%" }}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε οδό και αριθμό της διεύθυνσης χρέωσης. Επιτρέπονται έως 50 χαρακτήρες χωρίς τόνους Ελληνικοί και Αγγλικοί, πεζοί και κεφαλαίοι, το κενό και τα εξής σύμβολα: /:_().,+-",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <TextFieldElement
                    required
                    name={`zipCode`}
                    label={t("Τ.Κ.")}
                    sx={{ width: "100%" }}
                    inputProps={{
                      maxLength: 16,
                    }}
                  />
                  <Tooltip
                    title={t(
                      "Εισάγετε τον ταχυδρομικό κώδικα της διεύθυνσης χρέωσης. Επιτρέπονται έως 16 χαρακτήρες χωρίς τόνους Ελληνικοί και Αγγλικοί, πεζοί και κεφαλαίοι, το κενό και τα εξής σύμβολα: /:_().,+-",
                    )}
                  >
                    <HelpOutlinedIcon color="primary" />
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            <Typography
              sx={{ backgroundColor: "#d0f4fb", padding: "1em" }}
              mt={2}
              color="primary"
              variant="subtitle1"
            >
              {t(
                "Σας ενημερώνουμε πως για την υλοποίηση της υπηρεσίας πιστοποίησης κατόχου κάρτας ενδέχεται να απαιτηθεί η μεταφορά στοιχείων της κάρτας σας, σε τρίτο πάροχο, συνεργαζόμενο με την ΤΡΑΠΕΖΑ ΠΕΙΡΑΙΩΣ.",
              )}
            </Typography>
            {mode === "violationCase" && (
              <PaymentCheckbox
                checked={paymentAccepted}
                onCheckedChange={setPaymentAccepted}
              />
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <LoadingButton
                variant="contained"
                type="submit"
                loading={isLoadingSumbit}
                disabled={
                  backendData?.data?.maintenance ||
                  !backendData?.data?.creditCardActive ||
                  (mode === "violationCase" && !paymentAccepted)
                }
              >
                {t("Εκτέλεση Πληρωμής")}
              </LoadingButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              {(backendData?.data?.maintenance ||
                backendData?.data?.diasInProgress) && (
                <InfoMessage
                  message={`${t("Το σύστημα είναι σε συντήρηση και οι πληρωμές έχουν απενεργοποιηθεί.")} ${t("Παρακαλώ ξαναπροσπαθήστε σε λίγα λεπτά, όταν το σύστημα θα είναι σε πλήρη λειτουργία.")}`}
                  variant={MessageVariant.Error}
                />
              )}
              {!backendData?.data?.creditCardActive && (
                <>
                  <InfoMessage
                    message={t(
                      "Το σύστημα πληρωμών μέσω πιστωτικής κάρτας είναι προσωρινά μη διαθέσιμο.",
                    )}
                    variant={MessageVariant.Error}
                  />
                </>
              )}
            </Box>
          </Box>
          <Dialog open={Dialog3DSOpen}>
            <DialogContent
              sx={{
                width: "auto",
                height: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "1rem",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <Typography>
                  {t(
                    "Παρακαλώ περιμένετε όσο γίνεται η επικοινωνία με την Τράπεζα",
                  )}
                  ...
                </Typography>
                <Typography>{t("Μην κλείσετε το παράθυρο!")}</Typography>
                <CircularProgress />
              </Box>
              {isAcsChallenge ? (
                <iframe
                  title="3D Secure"
                  srcDoc={iframeContent}
                  style={{ width: 400, height: 600, border: 0 }}
                />
              ) : (
                <InnerHTML html={iframeContent} allowRerender={true} />
              )}
            </DialogContent>
          </Dialog>
        </FormContainer>
      </Box>
    </>
  );
}
export default PaymentForm;
